import React from "react";
import { ChevronDown, ListFilter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {
  AccessGroupFilterOption,
  AccessGroupScope,
} from "@/types/access-group";

export type GroupsFiltersState = {
  entityIds: string[];
  userIds: string[];
};

export const DEFAULT_GROUPS_FILTERS: GroupsFiltersState = {
  entityIds: [],
  userIds: [],
};

type GroupsFilterSelectionKey = "entityIds" | "userIds";

type GroupsFiltersPopoverProps = {
  applied: GroupsFiltersState;
  onApply: (next: GroupsFiltersState) => void;
  scope: AccessGroupScope;
  entityOptions: AccessGroupFilterOption[];
  userOptions: AccessGroupFilterOption[];
};

const FILTER_CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const FILTER_COMBOBOX_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

export function GroupsFiltersPopover({
  applied,
  onApply,
  scope,
  entityOptions,
  userOptions,
}: GroupsFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<GroupsFiltersState>(applied);
  const [sectionOpen, setSectionOpen] = React.useState({
    tenant: false,
    users: false,
  });
  const [optionSearch, setOptionSearch] = React.useState({
    tenant: "",
    users: "",
  });

  const activeCount = draft.entityIds.length + draft.userIds.length;

  const syncDraftFromApplied = React.useCallback(() => {
    setDraft(applied);
    setOptionSearch({ tenant: "", users: "" });
    setSectionOpen((prev) => ({
      ...prev,
      tenant: applied.entityIds.length > 0,
      users: applied.userIds.length > 0,
    }));
  }, [applied]);

  const toggleOption = (key: GroupsFilterSelectionKey, value: string) => {
    setDraft((prev) => {
      const has = prev[key].includes(value);
      return {
        ...prev,
        [key]: has
          ? prev[key].filter((v) => v !== value)
          : [...prev[key], value],
      };
    });
  };

  const renderSelectableSection = ({
    field,
    openKey,
    options,
    selected,
    selectedKey,
    searchValue,
    onSearchChange,
  }: {
    field: "hotel" | "tenant" | "users";
    openKey: "tenant" | "users";
    options: AccessGroupFilterOption[];
    selected: string[];
    selectedKey: GroupsFilterSelectionKey;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
  }) => {
    const normalizedSearch = searchValue?.trim().toLocaleLowerCase() ?? "";
    const filteredOptions = normalizedSearch
      ? options.filter(
          (option) =>
            option.label.toLocaleLowerCase().includes(normalizedSearch) ||
            option.id.toLocaleLowerCase().includes(normalizedSearch),
        )
      : options;

    return (
      <div className="space-y-2">
        <Button
          type="button"
          variant="span"
          className={FILTER_COMBOBOX_TRIGGER_CLASS}
          onClick={() =>
            setSectionOpen((prev) => ({ ...prev, [openKey]: !prev[openKey] }))
          }
        >
          {t(`users.groups.filters.fields.${field}`)}
          <ChevronDown
            className={cn(
              "size-4 shrink-0 text-[#737373] transition-transform",
              sectionOpen[openKey] && "rotate-180",
            )}
          />
        </Button>

        {sectionOpen[openKey] ? (
          <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
            {onSearchChange ? (
              <div className="border-b border-[#E5E5E5] p-2">
                <Input
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={t("users.groups.filters.searchPlaceholder")}
                  aria-label={t(`users.groups.filters.searchAria.${field}`)}
                  className="h-8"
                />
              </div>
            ) : null}
            <div className="max-h-[176px] overflow-y-auto p-1">
              <div className="space-y-1">
                {filteredOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-[#737373]">
                    {t("users.groups.filters.noOptions")}
                  </div>
                ) : null}
                {filteredOptions.map((option) => {
                  const checked = selected.includes(option.id);
                  return (
                    <label
                      key={option.id}
                      className={cn(
                        "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                        checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() =>
                          toggleOption(selectedKey, option.id)
                        }
                        className={FILTER_CHECKBOX_CLASS}
                      />
                      <span className="text-sm leading-normal font-normal text-[#171717]">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) syncDraftFromApplied();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground h-8 min-h-8 w-fit gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          {t("users.groups.filters.button")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(560px,calc(100vh-120px))] w-[320px] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base leading-normal font-semibold text-[#171717]">
              {activeCount > 0
                ? t("users.groups.filters.titleWithCount", {
                    count: activeCount,
                  })
                : t("users.groups.filters.title")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("users.groups.filters.aria.close")}
            >
              <X className="size-5" />
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
          {renderSelectableSection({
            field: scope === "tenant" ? "hotel" : "tenant",
            openKey: "tenant",
            options: entityOptions,
            selected: draft.entityIds,
            selectedKey: "entityIds",
            searchValue: optionSearch.tenant,
            onSearchChange: (value) =>
              setOptionSearch((prev) => ({ ...prev, tenant: value })),
          })}
          {renderSelectableSection({
            field: "users",
            openKey: "users",
            options: userOptions,
            selected: draft.userIds,
            selectedKey: "userIds",
            searchValue: optionSearch.users,
            onSearchChange: (value) =>
              setOptionSearch((prev) => ({ ...prev, users: value })),
          })}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraft(DEFAULT_GROUPS_FILTERS);
              setOptionSearch({ tenant: "", users: "" });
              onApply(DEFAULT_GROUPS_FILTERS);
            }}
          >
            {t("users.groups.filters.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            {t("users.groups.filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
